/****************************************************
 * Module Name:  roleMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { Component } from "react";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Avatar, Box, Button, Chip, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import AddRoleModal from "./addRoleModal";
import RoleProxy from "../../utility/proxy/manager/permissionProxy/roleProxy";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import AddUserModal from "../master/addUserModal";
import PermissionProxy from "../../utility/proxy/manager/permissionProxy/permissionProxy";
import UserProxy from "../../utility/proxy/manager/userProxy";
import AddPermissionModal from "./addPermissionModal";
import CommonSnackbar from "../common/commonSnackBar";
import NoPermission from "../common/noPermission";
import PageTitle from "../common/pageTitle";

export class RoleMaster extends Component {
  constructor(props) {
    super(props);

    this.state = {
      role: [],
      showAddRoleModal: false,
      showAddUserModal: false,
      userId: "",
      loadingButton: false,
      showAddPermissionModal: false,
      permissionId: "",

      editOperation: false,
      sourceData: {},
      errorSnackbar: false,
      accesDenied: false,
      showRoleLoadSpinner: true,
      local_Permission: Constants.USER_PERMISSION.filter(
        (iPermission) =>
          iPermission.permissionid === Constants.PERMISSIONS.PERMISSION_ROLE
      ),
      anchorEl: null,
      openMenuId: null,
      openOwnersMenuId: null,
    };

    this.checkPermission()
  }

  componentDidMount() {
    PageTitle("PERMISSION_ROLE");
  }

  checkPermission = () => {
    let allowed = Utility.isReadAllowed(this.state.local_Permission)
    if (!allowed) {
      this.setState({
        accesDenied: true
      })
    } else {
      this.fetchRoleData(false);
    }
  }

  fetchRoleData = (bAnimateSpinner) => {
    bAnimateSpinner && this.setState({ showRoleLoadSpinner: true });
    RoleProxy.GetAllRoleDetail((responseData) => {
      if (responseData) {
        this.setState({ role: responseData, showRoleLoadSpinner: false })
      } else {
        this.setState({ errorSnackbar: true, showRoleLoadSpinner: false })
      }
    }
    );
  };

  handleClick = (event, id) => {
    this.setState({
      anchorEl: event.currentTarget,
      openMenuId: id,
    });
  };

  handleOwnerMenuClick = (event, id) => {
    this.setState({
      anchorEl: event.currentTarget,
      openOwnersMenuId: id,
    });
  };

  handleClose = () => {
    this.setState({
      anchorEl: null,
      openMenuId: null,
    });
  };

  handleOwnerMenuClose = () => {
    this.setState({
      anchorEl: null,
      openOwnersMenuId: null,
    });
  };

  columns = [
    {
      field: "id",
      headerName: "ID",
      headerAlign: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      align: "center",
      renderCell: renderCellExpand,
    },
    {
      field: "name",
      headerName: "NAME",
      headerAlign: "center",
      align: "center",
      flex: 1.5,
      renderCell: renderCellExpand,
    },
    {
      field: "description",
      headerName: "DESCRIP",
      headerAlign: "center",
      align: "center",
      flex: 2,
      renderCell: renderCellExpand,
    },
    {
      field: "created",
      headerAlign: "center",
      align: "center",
      headerName: "CREATED AT",
      flex: 2,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    },
    {
      field: "rolepermissions",
      headerName: "PERMISSIONS",
      headerAlign: "center",
      align: "center",
      flex: 3,
      renderCell: (params) => (
        <Box>
          <Button
            aria-controls={this.state.openMenuId === params.id ? 'role-permissions-menu' : undefined}
            aria-haspopup="true"
            onClick={(event) => this.handleClick(event, params.id)}
          >
            View Permissions
          </Button>
          <Menu
            id="role-permissions-menu"
            anchorEl={this.state.anchorEl}
            open={this.state.openMenuId === params.id}
            onClose={this.handleClose}
            sx={{
              height: "40rem"
            }}
          >
            {params.value.map((item, index) => (
              <MenuItem
                key={index}
              >
                <Typography variant="body2">
                  {item.name}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      ),
      disableExport: true,
    },
    {
      field: "roleowners",
      headerName: "Owners",
      headerAlign: "center",
      align: "center",
      flex: 3,
      renderCell: (params) => (
        <Box>

          <Button
            aria-controls={this.state.openOwnersMenuId === params.id ? 'role-owners-menu' : undefined}
            aria-haspopup="true"
            onClick={(event) => this.handleOwnerMenuClick(event, params.id)}
          >
            View Owners
          </Button>
          <Menu
            id="role-owners-menu"
            anchorEl={this.state.anchorEl}
            open={this.state.openOwnersMenuId === params.id}
            onClose={this.handleOwnerMenuClose}
            sx={{
              height: "40rem"
            }}
          >
            {params.value.map((item, index) => (
              <MenuItem>
                <Chip
                  key={index}
                  avatar={<Avatar>{item.userid}</Avatar>}
                  label={item.username}
                  onClick={() => {
                    this.setState({
                      showAddUserModal: true,
                      editOperation: false,
                      sourceData: undefined,
                      userId: item.userid,
                      anchorEl: null,
                      openOwnersMenuId: null
                    });
                  }}
                />
              </MenuItem>
            ))}
          </Menu>
        </Box>
      ),
      disableExport: true,
    },
    {
      field: "edit",
      headerName: "EDIT",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Box
            style={{
              textAlign: "center",
              cursor: "pointer",
              display: params.row.roleowners.find(
                (owner) => owner.userid === Constants.loggedInUser.id
              )
                ? "block"
                : "none",
            }}
            onClick={() => {
              this.setState({
                showAddRoleModal: true,
                editOperation: true,
                sourceData: params.row,
              });
            }}
          >
            {" "}
            <BorderColorIcon color="primary" />
          </Box>
        );
      },
    },
  ];

  hideAddRoleModal = () => {
    this.setState({
      showAddRoleModal: false,
    });
  };

  hideAddPermissionModal = () => {
    this.setState({
      showAddPermissionModal: false,
    });
  };

  handleValidation = (roleData) => {
    //TODO do validation if needed here
    return true;
  };

  addRole = (roleData) => {
    this.setState({ loadingButton: true });
    if (this.state.editOperation) {
      //Updating Role, and once role is updated on call refresh the list of current role
      RoleProxy.UpdateRole(roleData, (newRole) => {
        if (newRole) {
          this.fetchRoleData(true);
          this.setState({ loadingButton: false });
          //Hidding role modal
          this.hideAddRoleModal();
        } else {
          this.setState({ errorSnackbar: true });
          this.setState({ loadingButton: false });
        }
      });
    } else {
      //Creating new Role, and once role is created on call refresh the list of current role
      RoleProxy.AddRole(roleData, (newRole) => {
        if (newRole) {
          this.fetchRoleData(true);
          this.setState({ loadingButton: false });
          //Hidding role modal
          this.hideAddRoleModal();
        } else {
          this.setState({ errorSnackbar: true });
          this.setState({ loadingButton: false });
        }
      });
    }
  };

  hideAddUserModal = () => {
    this.setState({ showAddUserModal: false });
  };

  editUser = (userData) => {
    this.setState({ loadingButton: true });
    UserProxy.UpdateUser(userData, (responseData) => {
      if (!responseData) {
        this.setState({ errorSnackbar: true });
        this.setState({ loadingButton: false });
      } else {
        this.setState({ loadingButton: false });
      }
    });
    this.hideAddUserModal();
  };

  editPermission = (data) => {
    this.setState({ loadingButton: true });
    PermissionProxy.UpdatePermission(data, (responseData) => {
      if (!responseData) {
        this.setState({ errorSnackbar: true });
        this.setState({ loadingButton: false });
      } else {
        this.setState({ loadingButton: false });
      }
    });
    this.hideAddPermissionModal();
  };

  render() {
    const CustomToolbar = () => {
      return (
        <GridToolbarContainer
          sx={{
            display: "flex",
            alignItems: "center",
            marginHorizontal: "5px",
            "&.MuiDataGrid-toolbarContainer": {
              padding: "0px 4px !important",
            },
          }}
        >
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarExport />
          <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
          {this.state.showRoleLoadSpinner ? (
            <CircularProgress size="1rem" />
          ) : (
            <Tooltip title="Reload List">
              <RefreshOutlinedIcon
                color="primary"
                fontSize="medium"
                onClick={() => this.fetchRoleData(true)}
                sx={{ cursor: "pointer" }}
              />
            </Tooltip>
          )}

          {Utility.isAddAllowed(this.state.local_Permission) ? (
            <Tooltip title="Add Role">
              <Button
                color="info"
                variant="outlined"
                size="small"
                sx={{ ml: "5px" }}
                onClick={() =>
                  this.setState({
                    showAddRoleModal: true,
                    editOperation: false,
                    sourceData: {},
                  })
                }
                startIcon={<AddBoxOutlinedIcon size="large" />}
              >
                Role
              </Button>
            </Tooltip>
          ) : null}

          <GridToolbarQuickFilter debounceMs={500} />
        </GridToolbarContainer>
      );
    }
    let allowed = Utility.isReadAllowed(this.state.local_Permission)
    if (!allowed) {
      return <NoPermission />
    }
    const rows = this.state.role;
    const columns = this.columns;
    return (
      <Box>
        <CommonSnackbar
          show={this.state.errorSnackbar}
          message={<Box>{Constants.APiFailErrorMessage}</Box>}
          variant={"error"}
          autoHide={Constants.SnackBarCloseTimer}
          handleClose={() => this.setState({ errorSnackbar: false })}
          title="Failed"
        />
        <br />
        {this.state.showAddRoleModal ? (
          <AddRoleModal
            Show={true}
            handleClose={this.hideAddRoleModal}
            handleSave={this.addRole}
            handleValidation={this.handleValidation}
            editOperation={this.state.editOperation}
            sourceData={this.state.sourceData}
            loading={this.state.loadingButton}
          />
        ) : (
          <Box />
        )}
        {this.state.showAddUserModal ? (
          <AddUserModal
            Show={true}
            handleClose={this.hideAddUserModal}
            handleSave={this.editUser}
            handleValidation={this.handleValidation}
            editOperation={this.state.editOperation}
            sourceData={this.state.sourceData}
            userId={this.state.userId}
            loading={this.state.loadingButton}
            saveButton={true}
          />
        ) : (
          <Box />
        )}
        {this.state.showAddPermissionModal ? (
          <AddPermissionModal
            Show={true}
            handleClose={this.hideAddPermissionModal}
            handleSave={this.editPermission}
            handleValidation={this.handleValidation}
            editOperation={this.state.editOperation}
            sourceData={this.state.sourceData}
            permissionId={this.state.permissionId}
            loading={this.state.loadingButton}
          />
        ) : (
          <Box />
        )}
        <Box px={1}>
          <Box
            sx={{
              backgroundColor: '#fff',
              color: '#000',
              width: "100%",
              position: "relative",
              "& .MuiTablePagination-selectLabel": {
                marginTop: "15px",
              },
              "& .MuiTablePagination-displayedRows": {
                marginTop: "15px",
              },
              "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                fontWeight: Constants.defaultFontWeight,
              },
              "& .css-cc8tf1": {
                fontWeight: Constants.defaultFontWeight,
              },
              height: "85vh",
            }}
          >
            <DataGridPro
              disableRowSelectionOnClick={true}
              getRowHeight={() => "auto"}
              sx={{
                '& .MuiDataGrid-columnHeader': {
                  color: '#fff',
                  backgroundColor: 'grey',
                },
                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                  py: "4px",
                },
                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                  py: "12px",
                },
                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                  py: "18px",
                },

                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
              }}
              density="compact"
              slots={{ toolbar: CustomToolbar }}
              loading={this.state.showRoleLoadSpinner}
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    // Hide columns id, the other columns will remain visible
                    id: false,
                  },
                },
                sorting: {
                  sortModel: [{ field: "id", sort: "desc" }],
                },
                pagination: {
                  paginationModel: Constants.pageSize,
                },
              }}
              pageSizeOptions={Constants.pageSizeOptions}
              pagination
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }
}

export default RoleMaster;
